import {useState} from 'react';
import {useTranslations} from 'next-intl';
import cx from 'classnames';
import Container from '@/container';
import {Row, Col} from '@/grid';
import BoxGrid from '@/ui/box-grid';
import PictureWrapper from '@/ui/images-wrapper';
import HighlightBox from '@/ui/highlight-box';
import {P, PNoMargin} from '@/typography';
import {withBoldWeight, withHeadingEyebrow} from '@/hocs/withFontType';
import {withNeutral50} from '@/hocs/withColors';
import {withMarginBottomXlToSm} from '@/hocs/withSpacing';
import type {AgentStep} from '@/content/workable-ai/types';
import {
  Grid,
  H2StepTitle,
  IntroNeutral50,
  StepsDesktop,
  StepsMobile,
  TitleBlock
} from '@/content/workable-ai/shared';
import styles from './how-agent-works-accordion.module.scss';

const IMG_PATH = '/static/images/workable-ai/steps/';
const STEP_IMAGE_WIDTH = 560;
const STEP_IMAGE_HEIGHT = 668;
const AGENT_STEP_PANEL_ID = 'agent-step-panel';
const EyebrowNeutral50 = withNeutral50(withHeadingEyebrow(P as never));
const BoldSpan = withBoldWeight('span' as never);
const DivMargined = withMarginBottomXlToSm('div' as never);

const HowAgentWorksAccordion = () => {
  const t = useTranslations('workableAi.howAgentWorks');
  const steps = t.raw('steps') as AgentStep[];
  const [activeIndex, setActiveIndex] = useState(0);
  const activeStep = steps[activeIndex];

  return (
    <section className={styles['steps-section']} aria-labelledby="how-agent-works-heading">
      <Container>
        <Grid fluid>
          <Row center="xs">
            <Col xs={12} md={10}>
              <EyebrowNeutral50>{t('eyebrow')}</EyebrowNeutral50>
              <H2StepTitle id="how-agent-works-heading">{t('title')}</H2StepTitle>
              <TitleBlock>
                <IntroNeutral50>{t('intro')}</IntroNeutral50>
              </TitleBlock>
            </Col>
          </Row>

          <DivMargined>
            <StepsMobile>
            <Row center="xs">
              <Col xs={12} sm={10}>
                <div className={styles['mobile-box-wrap']}>
                  <BoxGrid backgroundColor="var(--neutral50)">
                    <div className={styles['mobile-steps']}>
                      {steps.map(step => (
                        <div key={step.id}>
                          <p className={styles['mobile-step-label']}>{step.title}</p>
                          <p className={styles['mobile-step-body']}>{step.body}</p>
                        </div>
                      ))}
                    </div>
                  </BoxGrid>
                </div>
              </Col>
            </Row>
          </StepsMobile>

          <StepsDesktop>
            <Row>
              <Col xs={12}>
                <div className={styles.panel}>
                  <div className={styles['panel-inner']}>
                    <div
                      className={styles['tab-list']}
                      role="tablist"
                      aria-label={t('tabListAriaLabel')}>
                      {steps.map((step, index) => (
                        <button
                          key={step.id}
                          type="button"
                          role="tab"
                          aria-selected={index === activeIndex}
                          aria-controls={AGENT_STEP_PANEL_ID}
                          id={`agent-step-tab-${step.id}`}
                          className={cx(styles.tab, index === activeIndex && styles['tab-active'])}
                          onClick={() => setActiveIndex(index)}>
                          <span className={styles['tab-label']}>{step.title}</span>
                          <span className={styles['tab-body']}>{step.body}</span>
                        </button>
                      ))}
                    </div>
                    <div
                      id={AGENT_STEP_PANEL_ID}
                      role="tabpanel"
                      aria-labelledby={`agent-step-tab-${activeStep.id}`}
                      className={styles['image-shell']}>
                      <PictureWrapper
                        key={activeStep.id}
                        imgPath={IMG_PATH}
                        imageUrl={`step${activeStep.id}`}
                        imageTypeFallback="jpg"
                        altImage={activeStep.alt}
                        className={styles['image-rounded']}
                        width={STEP_IMAGE_WIDTH}
                        height={STEP_IMAGE_HEIGHT}
                      />
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            </StepsDesktop>
          </DivMargined>

          <Row center="xs">
            <Col xs={12}>
              <HighlightBox backgroundColor="var(--purple100)">
                <PNoMargin>
                  {t.rich('highlightBox.description', {
                    bold: chunks => <BoldSpan>{chunks}</BoldSpan>
                  })}
                </PNoMargin>
              </HighlightBox>
            </Col>
          </Row>
        </Grid>
      </Container>
    </section>
  );
};

export default HowAgentWorksAccordion;
